import React, { useState } from "react";
import axios from "../api";
import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";
import TransaccionalTabla from "./TransaccionalTabla";
import { esFormaPagoHubPagos, valorHubPagosFila } from "./transaccionalHubPagos";
import "../CierreDia/CierreDia.css";
import { formatMoneda, formatNumeroEnteroColombiano } from "../utils/formatMoneda";

const TransaccionalContainer = () => {
  const [filtros, setFiltros] = useState({
    centroCostos: "",
    codTerminal: "",
    idInicioTurno: "",
    fechaInicial: "",
    codEmpleado: "",
  });

  const [transacciones, setTransacciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [buscarPlaca, setBuscarPlaca] = useState("");
  const [buscarNumFactura, setBuscarNumFactura] = useState("");
  const history = useHistory();

  const totales = transacciones.reduce(
    (acc, t) => {
      const valorEf = Number(t?.valor_ef ?? 0) || 0;
      const valorDatafono = Number(t?.valor_datafono ?? 0) || 0;
      const total = Number(t?.total ?? 0) || 0;
      const hub = esFormaPagoHubPagos(t);

      return {
        valorEfectivo: acc.valorEfectivo + valorEf,
        valorDatafono: acc.valorDatafono + (hub ? 0 : valorDatafono),
        hubPagos: acc.hubPagos + (hub ? valorDatafono : 0),
        total: acc.total + total,
      };
    },
    { valorEfectivo: 0, valorDatafono: 0, hubPagos: 0, total: 0 },
  );

  const sumaDatafonoYHubPagos = totales.valorDatafono + totales.hubPagos;

  const normalizarTexto = (valor) => String(valor ?? "").trim();

  const construirDuplicados = (items, campo) => {
    const conteo = new Map();

    for (const item of items) {
      const valor = normalizarTexto(item?.[campo]);
      if (!valor) continue;
      conteo.set(valor, (conteo.get(valor) ?? 0) + 1);
    }

    return Array.from(conteo.entries())
      .filter(([, cantidad]) => cantidad > 1)
      .map(([valor, cantidad]) => ({ valor, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad || a.valor.localeCompare(b.valor));
  };

  const duplicadosPlaca = construirDuplicados(transacciones, "placa");
  const duplicadosFactura = construirDuplicados(transacciones, "num_factura");

  const facturasNumericas = transacciones
    .map((t) => normalizarTexto(t?.num_factura))
    .filter((n) => /^\d+$/.test(n))
    .map((n) => Number(n))
    .sort((a, b) => a - b);

  const facturasUnicas = Array.from(new Set(facturasNumericas));
  const facturasFaltantes = [];
  const transaccionesFiltradasPorPlaca = transacciones.filter((t) => {
    const placa = normalizarTexto(t?.placa).toLowerCase();
    const numFactura = normalizarTexto(t?.num_factura).toLowerCase();
    const criterioPlaca = normalizarTexto(buscarPlaca).toLowerCase();
    const criterioFactura = normalizarTexto(buscarNumFactura).toLowerCase();
    const cumplePlaca = !criterioPlaca || placa.includes(criterioPlaca);
    const cumpleFactura = !criterioFactura || numFactura.includes(criterioFactura);
    return cumplePlaca && cumpleFactura;
  });

  for (let i = 1; i < facturasUnicas.length; i += 1) {
    const anterior = facturasUnicas[i - 1];
    const actual = facturasUnicas[i];
    const diferencia = actual - anterior;

    if (diferencia > 1) {
      for (let faltante = anterior + 1; faltante < actual; faltante += 1) {
        facturasFaltantes.push(faltante);
      }
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const handleBuscar = async (e) => {
    e.preventDefault();
    const { centroCostos, codTerminal, idInicioTurno, fechaInicial, codEmpleado } = filtros;

    if (!centroCostos || !codTerminal) {
      alert(
        "Por favor digita centro de costos y código de terminal.",
      );
      return;
    }

    setCargando(true);
    try {
      const response = await axios.get(
        "/transaccional/por-centro-costos",
        {
          params: {
            centroCostos,
            codTerminal,
            idInicioTurno: idInicioTurno || undefined,
            fechaInicial: fechaInicial || undefined,
            codEmpleado: codEmpleado || undefined,
          },
          timeout: 60000, // 1 minuto de tiempo de espera
        },
      );
      setTransacciones(response.data);
    } catch (error) {
      console.error("Error al consultar transacciones:", error);
      alert(
        error.response?.data?.message ||
          error.message ||
          "Error al consultar transacciones.",
      );
      setTransacciones([]);
    } finally {
      setCargando(false);
    }
  };

  const handleLimpiar = () => {
    setFiltros({
      centroCostos: "",
      codTerminal: "",
      idInicioTurno: "",
      fechaInicial: "",
      codEmpleado: "",
    });
    setTransacciones([]);
    setBuscarPlaca("");
    setBuscarNumFactura("");
  };

  const handleVolverHome = () => {
    history.push('/Planillas');
  };

  const handleExportarExcel = () => {
    if (!transaccionesFiltradasPorPlaca.length) {
      alert("No hay datos para exportar con los filtros actuales.");
      return;
    }

    const fmt = formatNumeroEnteroColombiano;

    let sumEf = 0;
    let sumDatafono = 0;
    let sumHub = 0;
    let sumTotal = 0;

    const filas = transaccionesFiltradasPorPlaca.map((t) => {
      const ef = Number(t.valor_ef) || 0;
      const datafono = Number(t.valor_datafono) || 0;
      const hubRaw = valorHubPagosFila(t);
      const hubNum = hubRaw === "" ? 0 : Number(hubRaw);
      const totalRow = Number(t.total) || 0;
      const esHub = esFormaPagoHubPagos(t);

      sumEf += ef;
      if (!esHub) sumDatafono += datafono;
      sumHub += hubNum;
      sumTotal += totalRow;

      return {
        "Num. Factura": t.num_factura ?? "",
        Placa: t.placa ?? "",
        "Fecha inicial": t.fecha_inicial ?? "",
        "Fecha final": t.fecha_final ?? "",
        "Valor EF": fmt(ef),
        "Valor Datafono": esHub ? "" : fmt(datafono),
        "Hub de Pagos": hubRaw === "" ? "" : fmt(hubRaw),
        Total: fmt(totalRow),
        "Cod. Empleado": t.codEmpleado ?? "",
        "Cod. Terminal": t.codTerminal ?? "",
        "Cod. Park": t.codPark ?? "",
        "Id Inicio Turno": t.idInicioTurno ?? "",
      };
    });

    filas.push({
      "Num. Factura": "TOTAL",
      Placa: "",
      "Fecha inicial": "",
      "Fecha final": "",
      "Valor EF": fmt(sumEf),
      "Valor Datafono": fmt(sumDatafono),
      "Hub de Pagos": fmt(sumHub),
      Total: fmt(sumTotal),
      "Cod. Empleado": "",
      "Cod. Terminal": "",
      "Cod. Park": "",
      "Id Inicio Turno": "",
    });

    const hoja = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Transacciones");

    const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    XLSX.writeFile(libro, `transacciones_${fecha}.xlsx`);
  };

  return (
    <div className="cierre-turno-container">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h2 className="cierre-turno-title">Consulta Transaccional por Turno</h2>
        <button
          onClick={handleVolverHome}
          style={{
            padding: "10px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#0056b3")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#007bff")}
        >
          ← Volver al Home
        </button>
      </div>

      <form
        onSubmit={handleBuscar}
        className="cierre-turno-form"
        style={{ marginBottom: "20px" }}
      >
        <div className="form-row">
          <div className="form-group">
            <label>Centro de costos:</label>
            <input
              type="text"
              name="centroCostos"
              value={filtros.centroCostos}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Código terminal:</label>
            <input
              type="text"
              name="codTerminal"
              value={filtros.codTerminal}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Id inicio turno:</label>
            <input
              type="number"
              name="idInicioTurno"
              value={filtros.idInicioTurno}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Fecha inicial:</label>
            <input
              type="date"
              name="fechaInicial"
              value={filtros.fechaInicial}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Cod. empleado:</label>
            <input
              type="text"
              name="codEmpleado"
              value={filtros.codEmpleado}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-buttons">
          <button type="submit" className="buscar-btn" disabled={cargando}>
            {cargando ? "Buscando..." : "Buscar"}
          </button>
          <button
            type="button"
            className="cancelar-btn"
            onClick={handleLimpiar}
          >
            Limpiar
          </button>
        </div>
      </form>

      {transacciones.length > 0 && (
        <div className="table-container">
          <h3>Resumen de sumas</h3>
          <div className="resumen-totales-container">
            <table className="resumen-totales-table">
              <thead>
                <tr>
                  <th>Valor Efectivo</th>
                  <th>Valor Datafono</th>
                  <th>Hub de Pagos</th>
                  <th>Suma datafono y hub de pagos</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="valor-monetario resumen-valor">{formatMoneda(totales.valorEfectivo)}</td>
                  <td className="valor-monetario resumen-valor">{formatMoneda(totales.valorDatafono)}</td>
                  <td className="valor-monetario resumen-valor">{formatMoneda(totales.hubPagos)}</td>
                  <td className="valor-monetario resumen-valor">{formatMoneda(sumaDatafonoYHubPagos)}</td>
                  <td className="valor-monetario resumen-valor total-destacado">{formatMoneda(totales.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 style={{ marginTop: "20px" }}>Validaciones de calidad de datos</h3>
          <div className="resumen-totales-container">
            <table className="resumen-totales-table">
              <thead>
                <tr>
                  <th>Tipo validación</th>
                  <th>Valor</th>
                  <th>Cantidad / Detalle</th>
                </tr>
              </thead>
              <tbody>
                {duplicadosPlaca.length > 0 ? (
                  duplicadosPlaca.map((item) => (
                    <tr key={`placa-${item.valor}`}>
                      <td>Duplicado placa</td>
                      <td className="resumen-valor">{item.valor}</td>
                      <td className="resumen-valor">{item.cantidad} registros</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>Duplicado placa</td>
                    <td className="resumen-valor">-</td>
                    <td className="resumen-valor">Sin duplicados</td>
                  </tr>
                )}

                {duplicadosFactura.length > 0 ? (
                  duplicadosFactura.map((item) => (
                    <tr key={`factura-${item.valor}`}>
                      <td>Duplicado num_factura</td>
                      <td className="resumen-valor">{item.valor}</td>
                      <td className="resumen-valor">{item.cantidad} registros</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td>Duplicado Factura</td>
                    <td className="resumen-valor">-</td>
                    <td className="resumen-valor">Sin duplicados</td>
                  </tr>
                )}

                {facturasFaltantes.length > 0 ? (
                  <tr>
                    <td>Saltos facturación</td>
                    <td className="resumen-valor">{facturasFaltantes.join(", ")}</td>
                    <td className="resumen-valor">
                      {facturasFaltantes.length} números faltantes
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td>Saltos facturación</td>
                    <td className="resumen-valor">-</td>
                    <td className="resumen-valor">Sin saltos detectados</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <h3 style={{ margin: 0 }}>Resultados</h3>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Buscar por placa..."
                value={buscarPlaca}
                onChange={(e) => setBuscarPlaca(e.target.value)}
                style={{
                  minWidth: "220px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "0.95rem",
                }}
              />
              <input
                type="text"
                placeholder="Buscar por num_factura..."
                value={buscarNumFactura}
                onChange={(e) => setBuscarNumFactura(e.target.value)}
                style={{
                  minWidth: "220px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  fontSize: "0.95rem",
                }}
              />
              <button
                type="button"
                className="editar-btn"
                onClick={handleExportarExcel}
                style={{ padding: "10px 18px", fontSize: "0.95rem" }}
              >
                Exportar Excel
              </button>
            </div>
          </div>
          <TransaccionalTabla transacciones={transaccionesFiltradasPorPlaca} />
        </div>
      )}
    </div>
  );
};

export default TransaccionalContainer;

